import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, Button, Container, Card, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import AuthContext from '../context/AuthContext';

// Tarea 027 — modelo directo de AcceptInvitationPage.js, con una diferencia clave: el
// usuario ya expresó su intención al hacer clic en el link del correo, así que el POST
// de verificación se dispara automáticamente al montar, sin pedirle nada primero.
const VerifyEmailPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { authTokens, updateAuthTokens } = useContext(AuthContext);
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
    const [resendEmail, setResendEmail] = useState('');
    const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent'
    // Tarea 027c: distingue el mensaje de éxito según a dónde termine yendo el usuario.
    const [continuingIntoApp, setContinuingIntoApp] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const verify = async () => {
            try {
                await api.post(`/auth/verify-email/${token}`);
                if (cancelled) return;
                setStatus('success');

                // Tarea 027c: register ya deja al usuario con sesión iniciada, así que si este
                // navegador tiene esa sesión (el caso normal: abrió el link en el mismo
                // dispositivo), no tiene sentido pedirle que se loguee otra vez — se refresca
                // el token para que refleje la verificación y se le lleva directo a la app.
                if (authTokens?.refreshToken) {
                    try {
                        const refreshRes = await api.post('/auth/refresh', { refreshToken: authTokens.refreshToken });
                        if (cancelled) return;
                        updateAuthTokens(refreshRes.data);
                        setContinuingIntoApp(true);
                        setTimeout(() => { if (!cancelled) navigate('/sales'); }, 1500);
                        return;
                    } catch (refreshErr) {
                        // Sesión local inválida/vencida (u otro dispositivo) — cae al flujo normal.
                    }
                }

                setTimeout(() => {
                    if (!cancelled) navigate('/login', { state: { message: t('verifyEmailPage.success') } });
                }, 3000);
            } catch (err) {
                if (cancelled) return;
                setStatus('error');
            }
        };

        verify();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleResend = async (e) => {
        e.preventDefault();
        setResendStatus('sending');
        try {
            await api.post('/auth/resend-verification', { email: resendEmail });
        } finally {
            // Respuesta genérica anti-enumeración: siempre "enviado", exista o no la cuenta.
            setResendStatus('sent');
        }
    };

    return (
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
            <Card style={{ width: '100%', maxWidth: '420px' }}>
                <Card.Body>
                    <h2 className="text-center mb-4">{t('verifyEmailPage.title')}</h2>

                    {status === 'verifying' && (
                        <div className="text-center">
                            <Spinner animation="border" role="status" className="mb-3" />
                            <p>{t('verifyEmailPage.verifying')}</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <Alert variant="success">
                            {continuingIntoApp ? t('verifyEmailPage.successContinue') : t('verifyEmailPage.success')}
                        </Alert>
                    )}

                    {status === 'error' && (
                        <>
                            <Alert variant="danger">{t('verifyEmailPage.errors.invalidOrExpired')}</Alert>

                            {resendStatus === 'sent' ? (
                                <Alert variant="success">{t('loginPage.resendVerificationSent')}</Alert>
                            ) : (
                                <Form onSubmit={handleResend}>
                                    <p className="text-center">{t('verifyEmailPage.resendTitle')}</p>
                                    <Form.Group className="mb-3" controlId="resendEmail">
                                        <Form.Label>{t('verifyEmailPage.resendEmailLabel')}</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={resendEmail}
                                            onChange={(e) => setResendEmail(e.target.value)}
                                            placeholder={t('verifyEmailPage.resendEmailPlaceholder')}
                                            required
                                        />
                                    </Form.Group>
                                    <div className="d-grid">
                                        <Button variant="primary" type="submit" disabled={resendStatus === 'sending'}>
                                            {resendStatus === 'sending'
                                                ? <><Spinner as="span" animation="border" size="sm" /> {t('verifyEmailPage.resendSending')}</>
                                                : t('verifyEmailPage.resendButton')}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </>
                    )}

                    {/* Si ya está entrando a la app con su sesión, un enlace al login contradice el mensaje. */}
                    {(status === 'error' || (status === 'success' && !continuingIntoApp)) && (
                        <div className="text-center mt-3">
                            <Link to="/login">{t('verifyEmailPage.backToLogin')}</Link>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default VerifyEmailPage;
